import React, {useState} from 'react';
import {Layout, Drawer, Radio, Slider, Input, Tag} from "antd";
import {Button as NButton, Card, Container, Text} from "@nextui-org/react";
import {useCollection} from "react-firebase-hooks/firestore";
import {collection, getFirestore, query, where} from "firebase/firestore";
import {getFunctions, httpsCallable} from "firebase/functions";
import {useToast} from "@chakra-ui/react";
import moment from 'moment';
import Firebase from "../../Firebase/Firebase";
import Headerbar from "../../navBars/Headerbar";
import Sidebar from "../../navBars/Sidebar";
import {Link} from "react-router-dom";

const db = getFirestore(Firebase);
const {Content} = Layout;

const resolveRefundDispute = httpsCallable(getFunctions(Firebase), 'resolveRefundDispute');
const getDisputeChatThread = httpsCallable(getFunctions(Firebase), 'getDisputeChatThread');

function numberWithCommas(x: string | number) {
    return String(x).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function fmtDate(val: any) {
    if (!val) return '—';
    const d = typeof val.toDate === 'function' ? val.toDate() : val;
    return moment(d).format('DD MMM YYYY, HH:mm');
}

// receiptImages/shipmentPhotos elements are either a legacy plain URL string
// or the current {url, uploadedAt, removedAt, removedBy} shape (soft-delete
// stamp — deleteOrderPhoto never actually removes the photo, it only hides
// it from the seller's/buyer's normal view, so a removed item still needs to
// render here for dispute review, just visibly marked).
function photoUrl(photo: any): string {
    return typeof photo === 'string' ? photo : photo?.url ?? '';
}
function photoRemovedAt(photo: any): any {
    return typeof photo === 'string' ? null : photo?.removedAt ?? null;
}

function EvidenceThumbnails({photos, alt}: {photos: any[]; alt: string}) {
    if (!photos?.length) return null;
    return (
        <div style={{display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap'}}>
            {photos.map((photo: any, i: number) => {
                const url = photoUrl(photo);
                const removedAt = photoRemovedAt(photo);
                return (
                    <a key={i} href={url} target="_blank" rel="noreferrer" style={{position: 'relative'}}>
                        <img
                            src={url}
                            alt={alt}
                            style={{width: 80, height: 80, objectFit: 'cover', borderRadius: 6, opacity: removedAt ? 0.4 : 1}}
                        />
                        {removedAt && (
                            <div style={{
                                position: 'absolute', bottom: 0, left: 0, right: 0,
                                fontSize: 9, lineHeight: '12px', color: '#fff', background: 'rgba(0,0,0,0.65)',
                                borderRadius: '0 0 6px 6px', padding: '1px 3px', textAlign: 'center',
                            }}>
                                Removed {fmtDate(removedAt)}
                            </div>
                        )}
                    </a>
                );
            })}
        </div>
    );
}

type ChatMessage = {
    id: string;
    text: string;
    type: string;
    authorId: string;
    uri: string | null;
    duration: number | null;
    productId: string | null;
    productName: string | null;
    productImage: string | null;
    productPrice: string | null;
    originalPrice: string | null;
    offerPrice: string | null;
    quantity: number | null;
    createdAt: string | null;
};

// A single checkout can split into several sibling order documents (one per
// distinct product within a seller's slice — see createCartOrders/
// moveFilteredCartsToPurchase), stamped orderNo `${root}-${i}`. Surfaces the
// other parts as adjudication context: a dispute on one item shouldn't be
// read in isolation from what else the buyer ordered in the same checkout.
function rootOrderNo(orderNo: string): string {
    return (orderNo ?? '').replace(/-\d+$/, '');
}

function SiblingOrdersNote({order}: {order: any}) {
    const root = rootOrderNo(order.orderNo ?? '');
    const [snapshot] = useCollection(
        root
            ? query(collection(db, 'orders'), where('orderNo', '>=', root), where('orderNo', '<', root + ''))
            : null,
    );
    const siblings = (snapshot?.docs ?? [])
        .map((d) => ({orderId: d.id, ...d.data()} as any))
        .filter((s) => s.orderId !== order.orderId);
    if (siblings.length === 0) return null;
    return (
        <div style={{fontSize: 12, color: '#666'}}>
            Also from this checkout: {siblings.map((s, i) => (
                <span key={s.orderId}>
                    #{(s.orderNo ?? s.orderId).slice(-6)}{i < siblings.length - 1 ? ', ' : ''}
                </span>
            ))}
        </div>
    );
}

// ── Detail drawer — evidence review + resolution form ────────────────────────

const DisputeDrawer = ({order, open, onClose}: {order: any; open: boolean; onClose: () => void}) => {
    const toast = useToast();
    const [resolution, setResolution] = useState<'buyer' | 'seller' | 'split'>('buyer');
    const [sellerFaultPct, setSellerFaultPct] = useState(50);
    const [adminNote, setAdminNote] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [chatMessages, setChatMessages] = useState<ChatMessage[] | null>(null);
    const [loadingChat, setLoadingChat] = useState(false);

    if (!order) return null;

    const loadChat = async () => {
        setLoadingChat(true);
        try {
            const result: any = await getDisputeChatThread({orderId: order.orderId});
            setChatMessages(result.data?.messages ?? []);
        } catch (e: any) {
            toast({position: 'top', status: 'error', title: 'Failed to load chat', description: e?.message});
        } finally {
            setLoadingChat(false);
        }
    };

    const submitResolution = async () => {
        setSubmitting(true);
        try {
            await resolveRefundDispute({
                orderId: order.orderId,
                resolution,
                sellerFaultPct: resolution === 'split' ? sellerFaultPct : undefined,
                adminNote: adminNote.trim() || undefined,
            });
            toast({position: 'top', status: 'success', title: 'Dispute resolved'});
            onClose();
        } catch (e: any) {
            toast({position: 'top', status: 'error', title: 'Failed to resolve dispute', description: e?.message});
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Drawer
            open={open}
            onClose={onClose}
            width={720}
            title={(
                <span>
                    Dispute — Order #{' '}
                    <Link to={`/transactions?orderId=${order.orderId}`} onClick={onClose} style={{color: '#2563eb'}}>
                        {order.orderNo ?? order.orderId}
                    </Link>
                </span>
            )}
            className="dispute-drawer"
            bodyStyle={{padding: '20px 24px'}}
        >
            <div style={{display: 'flex', flexDirection: 'column', gap: 20}}>

                {/* Amount + parties */}
                <div style={{display: 'flex', gap: 24, flexWrap: 'wrap'}}>
                    <div>
                        <div style={{fontSize: 11, color: '#aaa'}}>TOTAL AMOUNT</div>
                        <div style={{fontSize: 22, fontWeight: 700}}>MWK {numberWithCommas(order.totalAmount ?? 0)}</div>
                    </div>
                    <div style={{minWidth: 0}}>
                        <div style={{fontSize: 11, color: '#aaa'}}>BUYER</div>
                        <Link
                            to={`/buyers/${order.buyerId}`}
                            title={order.buyerId}
                            style={{fontSize: 13, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', color: '#2563eb'}}
                        >
                            {order.buyerId}
                        </Link>
                    </div>
                    <div style={{minWidth: 0}}>
                        <div style={{fontSize: 11, color: '#aaa'}}>SELLER</div>
                        <Link
                            to={`/shops/${order.sellerId}`}
                            title={order.sellerId}
                            style={{fontSize: 13, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', color: '#2563eb'}}
                        >
                            {order.sellerId}
                        </Link>
                    </div>
                    <div>
                        <div style={{fontSize: 11, color: '#aaa'}}>OPENED</div>
                        <div style={{fontSize: 13}}>{fmtDate(order.disputeOpenedAt)}</div>
                    </div>
                </div>

                <SiblingOrdersNote order={order} />

                {/* Order items */}
                <div>
                    <div style={{fontWeight: 600, marginBottom: 8}}>Order Items ({(order.items ?? []).length})</div>
                    {(!order.items || order.items.length === 0) ? (
                        <div style={{fontSize: 13, color: '#999'}}>No item details available.</div>
                    ) : order.items.map((item: any, i: number) => {
                        const Wrapper: any = item.productId ? Link : 'div';
                        return (
                            <Wrapper key={i} {...(item.productId ? {to: `/products/${item.productId}`} : {})} style={{
                                display: 'flex', alignItems: 'center', gap: 12,
                                background: '#fff', borderRadius: 8, padding: '10px 14px',
                                marginBottom: 8, border: '1px solid #eee',
                                color: 'inherit', textDecoration: 'none',
                                cursor: item.productId ? 'pointer' : 'default',
                            }}>
                                {item.imageUrl && (
                                    <img src={item.imageUrl} alt="" style={{width: 48, height: 48, objectFit: 'cover', borderRadius: 6, flexShrink: 0}} />
                                )}
                                <div style={{flex: 1}}>
                                    <div style={{fontWeight: 600, fontSize: 14, color: item.productId ? '#2563eb' : undefined}}>{item.productName ?? '—'}</div>
                                    <div style={{fontSize: 12, color: '#888', marginTop: 2}}>
                                        Qty: {item.quantity ?? 1}
                                        {item.price && ` · MWK ${numberWithCommas(item.price)}`}
                                        {item.selectedVariants && (
                                            <span style={{marginLeft: 8}}>
                                                {Object.entries(item.selectedVariants).map(([k, v]) => `${k}: ${v}`).join(', ')}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div style={{fontWeight: 600, fontSize: 14, color: '#29327b'}}>
                                    MWK {numberWithCommas((parseInt(item.quantity ?? 1) * parseFloat(item.price ?? 0)).toFixed(0))}
                                </div>
                            </Wrapper>
                        );
                    })}
                </div>

                {/* Buyer's claim */}
                <div style={{background: '#f7f8fc', borderRadius: 8, padding: 16}}>
                    <div style={{fontWeight: 600, marginBottom: 6}}>Buyer&apos;s refund request</div>
                    <div style={{fontSize: 13, color: '#333'}}>{order.refundReason || '—'}</div>
                    {order.refundReasonDetails && (
                        <div style={{fontSize: 12, color: '#666', marginTop: 4}}>{order.refundReasonDetails}</div>
                    )}
                    {order.refundPhotos?.length > 0 && (
                        <div style={{display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap'}}>
                            {order.refundPhotos.map((url: string, i: number) => (
                                <a key={i} href={url} target="_blank" rel="noreferrer">
                                    <img src={url} alt="Buyer's refund evidence" style={{width: 80, height: 80, objectFit: 'cover', borderRadius: 6}} />
                                </a>
                            ))}
                        </div>
                    )}
                </div>

                {/* Seller's response */}
                <div style={{background: '#f7f8fc', borderRadius: 8, padding: 16}}>
                    <div style={{fontWeight: 600, marginBottom: 6}}>Seller&apos;s response</div>
                    <div style={{fontSize: 13, color: '#333'}}>
                        {order.refundAcceptance === 'denied' ? 'Denied' : order.refundAcceptance === 'accepted' ? 'Accepted' : 'No response yet'}
                    </div>
                    {order.refundDenialReason && (
                        <div style={{fontSize: 12, color: '#666', marginTop: 4}}>{order.refundDenialReason}</div>
                    )}
                    {order.receiptImages?.length > 0 && (
                        <>
                            <div style={{fontSize: 11, color: '#aaa', marginTop: 10}}>COURIER RECEIPT</div>
                            <EvidenceThumbnails photos={order.receiptImages} alt="Courier receipt" />
                        </>
                    )}
                    {order.shipmentPhotos?.length > 0 && (
                        <>
                            <div style={{fontSize: 11, color: '#aaa', marginTop: 10}}>PRODUCT PHOTOS</div>
                            <EvidenceThumbnails photos={order.shipmentPhotos} alt="Seller's shipment" />
                        </>
                    )}
                </div>

                {/* Chat */}
                <div>
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8}}>
                        <div style={{fontWeight: 600}}>Chat between buyer and seller</div>
                        {chatMessages === null && (
                            <NButton size="sm" onClick={loadChat} disabled={loadingChat}>
                                {loadingChat ? 'Loading…' : 'Load chat'}
                            </NButton>
                        )}
                    </div>
                    {chatMessages !== null && (
                        chatMessages.length === 0 ? (
                            <div style={{fontSize: 13, color: '#999'}}>No messages between these two.</div>
                        ) : (
                            <div style={{maxHeight: 260, overflowY: 'auto', overflowX: 'hidden', display: 'flex', flexDirection: 'column', gap: 10}}>
                                {chatMessages.map((m) => {
                                    const isBuyer = m.authorId === order.buyerId;
                                    return (
                                        <div key={m.id} style={{alignSelf: isBuyer ? 'flex-start' : 'flex-end', maxWidth: '75%'}}>
                                            <div style={{
                                                fontSize: 10, fontWeight: 600, marginBottom: 2,
                                                textAlign: isBuyer ? 'left' : 'right',
                                                color: isBuyer ? '#4c5fd5' : '#666',
                                            }}>
                                                {isBuyer ? 'BUYER' : 'SELLER'}
                                            </div>
                                            <div style={{
                                                background: isBuyer ? '#eef1fb' : '#f0f0f0',
                                                borderRadius: 8, padding: '6px 10px',
                                            }}>
                                                {m.type === 'image' && m.uri ? (
                                                    <a href={m.uri} target="_blank" rel="noreferrer">
                                                        <img src={m.uri} alt="Chat attachment" style={{maxWidth: 160, maxHeight: 160, borderRadius: 6, display: 'block'}} />
                                                    </a>
                                                ) : m.type === 'audio' && m.uri ? (
                                                    <audio controls src={m.uri} style={{height: 34, maxWidth: 220}} />
                                                ) : m.type === 'product' ? (
                                                    <Link to={m.productId ? `/products/${m.productId}` : '#'} style={{display: 'flex', gap: 8, alignItems: 'center', color: 'inherit'}}>
                                                        {m.productImage && (
                                                            <img src={m.productImage} alt="" style={{width: 44, height: 44, objectFit: 'cover', borderRadius: 6, flexShrink: 0}} />
                                                        )}
                                                        <div>
                                                            <div style={{fontSize: 13, fontWeight: 500, color: '#2563eb'}}>{m.productName || 'Product'}</div>
                                                            {m.productPrice && <div style={{fontSize: 12, color: '#666'}}>MWK {m.productPrice}</div>}
                                                        </div>
                                                    </Link>
                                                ) : m.type === 'customOrder' ? (
                                                    <Link to={m.productId ? `/products/${m.productId}` : '#'} style={{display: 'flex', gap: 8, alignItems: 'center', color: 'inherit'}}>
                                                        {m.productImage && (
                                                            <img src={m.productImage} alt="" style={{width: 44, height: 44, objectFit: 'cover', borderRadius: 6, flexShrink: 0}} />
                                                        )}
                                                        <div>
                                                            <div style={{fontSize: 13, fontWeight: 500, color: '#2563eb'}}>{m.productName || 'Custom offer'}</div>
                                                            <div style={{fontSize: 12, color: '#666'}}>
                                                                {m.originalPrice && m.originalPrice !== m.offerPrice && (
                                                                    <span style={{textDecoration: 'line-through', marginRight: 6}}>MWK {m.originalPrice}</span>
                                                                )}
                                                                {m.offerPrice && <span>MWK {m.offerPrice}</span>}
                                                                {m.quantity ? ` × ${m.quantity}` : ''}
                                                            </div>
                                                        </div>
                                                    </Link>
                                                ) : (
                                                    <div style={{fontSize: 13}}>{m.text || '—'}</div>
                                                )}
                                            </div>
                                            <div style={{fontSize: 10, color: '#999', textAlign: isBuyer ? 'left' : 'right', marginTop: 2}}>
                                                {m.createdAt ? moment(m.createdAt).format('DD MMM, HH:mm') : ''}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )
                    )}
                </div>

                {/* Resolution */}
                {order.disputeStatus === 'resolved' ? (
                    <div style={{background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: 16}}>
                        <div style={{fontWeight: 600}}>Already resolved</div>
                        <div style={{fontSize: 13, marginTop: 4}}>
                            {order.disputeResolution === 'buyer' ? 'Refund approved (full)' : order.disputeResolution === 'split' ? `Split — ${order.disputeSellerFaultPct}% seller fault` : 'Refund denied'}
                        </div>
                        {order.disputeAdminNote && <div style={{fontSize: 12, color: '#666', marginTop: 4}}>{order.disputeAdminNote}</div>}
                    </div>
                ) : (
                    <div style={{borderTop: '1px solid #eee', paddingTop: 16}}>
                        <div style={{fontWeight: 600, marginBottom: 10}}>Resolution</div>
                        <Radio.Group value={resolution} onChange={(e) => setResolution(e.target.value)} style={{marginBottom: 12}}>
                            <Radio.Button value="buyer">Full refund (buyer)</Radio.Button>
                            <Radio.Button value="split">Split</Radio.Button>
                            <Radio.Button value="seller">Deny (seller)</Radio.Button>
                        </Radio.Group>

                        {resolution === 'split' && (
                            <div style={{marginBottom: 12}}>
                                <div style={{fontSize: 12, color: '#666', marginBottom: 4}}>
                                    Seller fault: {sellerFaultPct}% — buyer receives that share in full;
                                    the seller absorbs the payment fee on it. The rest is refunded minus its own fee.
                                </div>
                                <Slider min={0} max={100} value={sellerFaultPct} onChange={setSellerFaultPct} />
                            </div>
                        )}

                        <Input.TextArea
                            rows={3}
                            placeholder="Note for the record (optional, visible to buyer & seller)"
                            value={adminNote}
                            onChange={(e) => setAdminNote(e.target.value)}
                            style={{marginBottom: 12}}
                        />

                        <NButton color="primary" onClick={submitResolution} disabled={submitting} css={{width: '100%'}}>
                            {submitting ? 'Submitting…' : 'Submit Resolution'}
                        </NButton>
                    </div>
                )}
            </div>
        </Drawer>
    );
};

// ── Main page ──────────────────────────────────────────────────────────────

const Disputes = () => {
    const [collapsed, setCollapsed] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState<any>(null);
    const [showResolved, setShowResolved] = useState(false);

    const [snapshot, loading] = useCollection(
        query(collection(db, 'orders'), where('disputeStatus', 'in', showResolved ? ['open', 'resolved'] : ['open'])),
    );

    const rows = (snapshot?.docs ?? [])
        .map((d) => ({orderId: d.id, ...d.data()} as any))
        .sort((a, b) => (b.disputeOpenedAt?.toMillis?.() ?? 0) - (a.disputeOpenedAt?.toMillis?.() ?? 0));

    return (
        <>
            <Layout style={{minHeight: '100vh'}}>
                <Headerbar collapsed={collapsed} setCollapsed={setCollapsed}/>
                <Layout className="site-layout">
                    <Sidebar collapsed={collapsed}/>
                    <Content>
                        <Container className="mt-2">
                            <Card className="my-2 p-3 font-JosefinSans">
                                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8}}>
                                    <Text h4 size={23} css={{color: "#29327b", fontWeight: "$bold"}}>
                                        Refund Disputes
                                    </Text>
                                    <label style={{fontSize: 13, display: 'flex', alignItems: 'center', gap: 6}}>
                                        <input type="checkbox" checked={showResolved} onChange={(e) => setShowResolved(e.target.checked)} />
                                        Show resolved
                                    </label>
                                </div>

                                {loading ? (
                                    <div style={{padding: 40, textAlign: 'center', color: '#999'}}>Loading…</div>
                                ) : rows.length === 0 ? (
                                    <div style={{padding: 40, textAlign: 'center', color: '#999'}}>
                                        No {showResolved ? '' : 'open '}disputes.
                                    </div>
                                ) : (
                                    <div style={{marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8}}>
                                        {rows.map((order) => (
                                            <div
                                                key={order.orderId}
                                                onClick={() => setSelectedOrder(order)}
                                                style={{
                                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                                    padding: '12px 16px', border: '1px solid #eee', borderRadius: 8, cursor: 'pointer',
                                                    background: '#fff',
                                                }}
                                            >
                                                <div>
                                                    <div style={{fontWeight: 600, fontSize: 14}}>Order #{order.orderNo ?? order.orderId}</div>
                                                    <div style={{fontSize: 12, color: '#888'}}>{order.refundReason || 'No reason given'}</div>
                                                </div>
                                                <div style={{display: 'flex', alignItems: 'center', gap: 12}}>
                                                    <span style={{fontSize: 13}}>MWK {numberWithCommas(order.totalAmount ?? 0)}</span>
                                                    <Tag color={order.disputeStatus === 'open' ? 'gold' : 'green'}>
                                                        {order.disputeStatus === 'open' ? 'Open' : 'Resolved'}
                                                    </Tag>
                                                    <span style={{fontSize: 12, color: '#aaa'}}>{fmtDate(order.disputeOpenedAt)}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </Card>
                        </Container>
                    </Content>
                </Layout>
            </Layout>

            <DisputeDrawer
                order={selectedOrder}
                open={selectedOrder !== null}
                onClose={() => setSelectedOrder(null)}
            />
        </>
    );
};

export default Disputes;
